import { Button, Card, CardActions, CardContent, TextField } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import CustomLoader from "../../../../../../../../util/component/CustomLoader";
import { setSnackbar } from "../../../../../../../../util/state/globalAction";
import { GlobalContext } from "../../../../../../../../util/state/GlobalContextProvider";
import { snackbarTypes } from "../../../../../../../../util/state/globalVariables";
import { createBookingNote } from "../../../../../../api/services";
import { ReactQueryKey } from "../../../../../../types";
import { MAX_CHAR_BODY_NOTE } from "../../../../types";

const NewNote = ({
    show,
    onClose
}: {
    show: boolean,
    onClose: () => void,
}) => {

    const { t } = useTranslation('booking');

    const { id } = useParams();

    const { dispatchGlobalData } = useContext(GlobalContext);

    const queryClient = useQueryClient();

    const maxChar = MAX_CHAR_BODY_NOTE;

    const createBookingNoteMutation = useMutation(
        createBookingNote,
        {
            onSuccess: () => {
                queryClient.invalidateQueries([ReactQueryKey.BookingNotes]);
                queryClient.invalidateQueries([ReactQueryKey.BookingById, id]);
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.success.default'),
                            type: snackbarTypes.success,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            },
            onError: () => {
                dispatchGlobalData({
                    type: setSnackbar,
                    payload: {
                        snackbar: {
                            message: t('message.error.default'),
                            type: snackbarTypes.error,
                            show: true,
                            autoClose: true,
                        }
                    }
                });
            },
            onSettled: () => {
                onClose();
            }
        }
    );

    const [body, setBody] = useState<string>('');

    // Use Callback
    const handleClickSave = useCallback(() => {
        if (!id) {
            return;
        }
        createBookingNoteMutation.mutate({
            bookingId: id,
            body: body
        });
    }, [body, id])

    // Use Effect
    useEffect(() => {
        if (!show)
            setBody('');
    }, [show])

    if (!show)
        return <></>;

    return (
        <>
            <Card
                variant="outlined"
                sx={{
                    borderColor: 'primary.main'
                }}
            >
                <CardContent>
                    <TextField
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        placeholder={t('label.placeholderNote')}
                        disabled={createBookingNoteMutation.isLoading}
                        autoFocus
                        fullWidth
                        multiline
                        maxRows={30}
                        InputProps={{
                            disableUnderline: true
                        }}
                        error={body.length > maxChar}
                        helperText={body.length + ' / ' + maxChar}
                        FormHelperTextProps={{
                            component: "div"
                        } as any}
                    />
                </CardContent>
                <CardActions>
                    <Button
                        onClick={handleClickSave}
                        disabled={body === '' || createBookingNoteMutation.isLoading}
                    >
                        {t('label.save')}
                    </Button>
                    <Button
                        variant="outlined"
                        onClick={onClose}
                        disabled={createBookingNoteMutation.isLoading}
                    >
                        {t('label.cancel')}
                    </Button>
                </CardActions>
            </Card>
            <CustomLoader loading={createBookingNoteMutation.isLoading} />
        </>
    );

}

export default NewNote;
